import { useState, useMemo, useEffect, Suspense, lazy } from 'react'
import firebase from '../../firebase/compat'
import { db } from '../../firebase/config'
import { useFirestoreGeneralOrderBy } from '../../firebase/useFirestore'
import AnalysisItemNavigator from '../analysis/AnalysisItemNavigator'
import DashboardCardOptions from '../dashboard/DashboardCardOptions'
import DashboardPersonaFilter from '../dashboard/DashboardPersonaFilter'
import DashboardFilterBar from '../dashboard/DashboardFilterBar'
import usePersonaResponseCounts from '../../hooks/usePersonaResponseCounts'
import useIndicatorCardControls from '../../hooks/useIndicatorCardControls'
import { getDefaultGraphType } from '../../helpers/visualisationDefaults'
import useChartColors from '../../hooks/useChartColors'
import filterTemplateEffectsResponses from '../../helpers/liveReportHelpers/filterTemplateEffectsResponses'
import { EmptyStateProvider, useEmptyStateContext } from '../../StateManagment/EmptyStateContext'
import periodEmptyState from '../../helpers/liveReportHelpers/periodEmptyState'
import isQuestionField from '../../helpers/questionnaire/isQuestionField'
import ResearchFieldHeading from './ResearchFieldHeading'

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// Shared visualisation engine for Research data. Used by both
// `AnalysisResearchCardBody` (dashboard) and `ResearchWidget` (live-report).
//
// displayMode: 'navigator' (default) one item with prev/next; 'stacked' shows
// every question vertically.
// nestedSettings: per-question overrides keyed by stable question ID.
//   `visible: false` drops the question from this widget only (inbox #242).
// templateData: when set, questions are filtered from the template blob
// instead of subscribing to Firestore, and threaded into ResearchData.

const ResearchData = lazy(() => import('../Research/ResearchData'))

/**
 * @param {object} props
 * @param {object} props.research
 * @param {string} [props.graphTypeOverride]
 * @param {string[]} [props.colorPalette]
 * @param {Object<string, string>} [props.colorOverrides]
 * @param {Array<object>} [props.personas]
 * @param {boolean} [props.personasEnabled]
 * @param {string} [props.selectedPersonaId]
 * @param {(personaId: string) => void} [props.onPersonaChange]
 * @param {(currentField: object, research: object) => React.ReactNode} [props.renderItemAction]
 * @param {'navigator'|'stacked'} [props.displayMode]
 * @param {boolean} [props.showFieldQuestion] - Zet de vraag (bij een meetstandaardvraag: het effect) als kop boven elke grafiek. Voor een live rapportage, waar ResearchData zijn eigen kop verbergt; dashboard en analyse laten hem uit.
 * @param {Object<string, Object<string, any>>} [props.nestedSettings]
 * @param {object|null} [props.templateData]
 * @param {Date|null} [props.startDate] - Period lower bound for the live data layer
 * @param {Date|null} [props.endDate] - Period upper bound (end of day) for the live data layer
 * @param {{start: string|null, end: string|null}|null} [props.periodRange] - Resolved period as ISO dates; filters template-blob rows client-side
 * @param {boolean} [props.hideControls] - Suppress this component's own chrome (filter bar, item navigator, options footer) so a wrapping ComparisonFrame can own them once. See docs/features/compare-two-periods.md
 * @param {number} [props.visible] - Controlled item index (compare mode drives both periods with one shared navigator). Omit for internal navigation.
 * @param {React.Dispatch<React.SetStateAction<number>>} [props.onVisibleChange] - Setter for the controlled item index.
 * @param {(items: Array<object>) => void} [props.onItemsChange] - Reports the resolved item list so an external navigator knows the count.
 * @returns {JSX.Element}
 */
const ResearchVisualization = ({
    research,
    graphTypeOverride,
    colorPalette,
    colorOverrides,
    personas,
    personasEnabled,
    selectedPersonaId,
    onPersonaChange,
    renderItemAction,
    displayMode = 'navigator',
    showFieldQuestion = false,
    nestedSettings,
    dashboardItem = null,
    onSaveOptions,
    templateData,
    onCurrentItemChange,
    graphColor,
    startDate = null,
    endDate = null,
    periodRange = null,
    hideControls = false,
    visible: controlledVisible,
    onVisibleChange,
    onItemsChange,
}) => {
    // Navigation can be controlled (compare mode: one shared navigator drives
    // both periods) or internal (normal single-chart cards).
    const [internalVisible, setInternalVisible] = useState(0)
    const visible = controlledVisible != null ? controlledVisible : internalVisible
    const setVisible = onVisibleChange || setInternalVisible
    // sankeyAvailable is published by the chart layer (MatrixData) and read by
    // the graph-type picker; both render inside this component, so the state
    // lives here and merges with the parent card's empty-state context.
    const parentEmptyState = useEmptyStateContext()
    const [sankeyAvailable, setSankeyAvailable] = useState(false)
    /* With a period active the generic "gegevens verschijnen zodra ze
       beschikbaar zijn" is a false promise — the data may already exist just
       outside the window, and the window itself is nowhere on screen.
       AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */
    const emptyStateValue = useMemo(
        () => ({
            ...parentEmptyState,
            ...(periodEmptyState(startDate, endDate) || {}),
            sankeyAvailable,
            setSankeyAvailable,
        }),
        [parentEmptyState, sankeyAvailable, startDate, endDate]
    )
    const { palette } = useChartColors({ colorPalette })

    const isTemplate = !!templateData

    // Template mode: period-filter the blob's response rows once (see
    // filterTemplateEffectsResponses). Live mode filters via startDate/endDate.
    const periodTemplateData = useMemo(
        () => filterTemplateEffectsResponses(templateData, periodRange),
        [templateData, periodRange]
    )

    const liveFields = useFirestoreGeneralOrderBy(
        'QuestionnaireFields',
        'QuestionnaireID',
        isTemplate ? '' : (research.QuestionnaireID || '123'),
        'Position',
        'asc'
    )

    const allFields = useMemo(() => {
        if (isTemplate) {
            const all = templateData?.data?.effects?.indicators || []
            return all
                .filter(i => i.QuestionnaireID === research?.QuestionnaireID && isQuestionField(i))
                .slice()
                .sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0))
        }
        return Array.isArray(liveFields) ? liveFields.filter(isQuestionField) : []
    }, [isTemplate, liveFields, templateData, research?.QuestionnaireID])

    // A string key, not `nestedSettings` itself: the widget passes a fresh
    // `{}` per render, which would rebuild `fields` and re-fire onItemsChange.
    // AI-CONTEXT: See docs/pitfalls.md#state-useeffect-inline-filter
    const hiddenKey = Object.entries(nestedSettings || {})
        .filter(([, item]) => item?.visible === false)
        .map(([id]) => id)
        .sort()
        .join('|')

    const fields = useMemo(() => {
        if (!hiddenKey) return allFields
        const hidden = new Set(hiddenKey.split('|'))
        return allFields.filter(field => !hidden.has(field.ID))
    }, [allFields, hiddenKey])

    // Hiding the question in view shortens the list under the navigator.
    useEffect(() => {
        if (fields.length > 0 && visible > fields.length - 1) setVisible(0)
    }, [fields.length, visible, setVisible])

    const currentField = fields[visible] || fields[0]

    // 'Geen resultaten gevonden' would claim the research has no data.
    const emptyText = allFields.length > 0
        ? 'Alle vragen van dit onderzoek staan uit in deze rapportage.'
        : 'Geen resultaten gevonden'

    // Surface the active carousel item so external UI can mirror navigation.
    useEffect(() => {
        if (!onCurrentItemChange) return
        onCurrentItemChange(displayMode === 'navigator' ? (currentField || null) : null)
    }, [currentField, displayMode, onCurrentItemChange])

    // Surface the full item list so a shared (compare) navigator knows the count.
    useEffect(() => {
        if (onItemsChange) onItemsChange(fields)
    }, [fields, onItemsChange])

    const showPersonaFilter = Boolean(personasEnabled) && Array.isArray(personas) && personas.length > 0
    const personaResponseCounts = usePersonaResponseCounts({
        personas: showPersonaFilter ? personas : null,
        researchId: showPersonaFilter ? research.ID : null,
    })

    const { resolvedFieldType, subItems } = useIndicatorCardControls(currentField, templateData)

    const resolveGraphType = (field) => {
        if (!field) return graphTypeOverride || 'bar'
        return nestedSettings?.[field.ID]?.graphType
            || dashboardItem?.GraphTypeByIndicator?.[field.ID]
            || graphTypeOverride
            || getDefaultGraphType(field.Type)
    }

    const resolveColorOverrides = (field) => {
        if (!field) return colorOverrides
        const widgetColor = nestedSettings?.[field.ID]?.color
        if (widgetColor) return { [field.ID]: widgetColor, ...(colorOverrides || {}) }
        return dashboardItem?.ColorOverridesByIndicator?.[field.ID] || colorOverrides
    }

    const saveIndicatorOptions = async (patch) => {
        // Analyse-page (transient) path — persist to caller state, not Firestore.
        if (onSaveOptions) {
            onSaveOptions(patch, currentField?.ID)
            return
        }
        if (!dashboardItem?.docid || !currentField?.ID) return
        const update = { Timestamp: firebase.firestore.FieldValue.serverTimestamp() }
        if (Object.prototype.hasOwnProperty.call(patch, 'GraphType')) {
            update[`GraphTypeByIndicator.${currentField.ID}`] = patch.GraphType
        }
        if (Object.prototype.hasOwnProperty.call(patch, 'ColorOverrides')) {
            update[`ColorOverridesByIndicator.${currentField.ID}`] = patch.ColorOverrides
        }
        await db.collection('DashboardItems').doc(dashboardItem.docid).update(update)
    }

    const renderOneField = (field) => (
        <>
        {showFieldQuestion && <ResearchFieldHeading field={field} />}
        <Suspense fallback={null}>
            <ResearchData
                fieldType={field.Type}
                fieldId={field.ID}
                researchId={research.ID}
                researchTitle={research.Title}
                graphType={resolveGraphType(field)}
                graphColor={graphColor}
                personaId={selectedPersonaId || null}
                paragrapgMomentVisibilityStyle={field.ParagraphMomentVisibilityStyle}
                colorPalette={palette}
                colorOverrides={resolveColorOverrides(field)}
                startDate={startDate}
                endDate={endDate}
                templateData={periodTemplateData || null}
            />
        </Suspense>
        </>
    )

    return (
        <EmptyStateProvider value={emptyStateValue}>
            {!hideControls && showPersonaFilter && (
                <DashboardFilterBar
                    onClear={() => onPersonaChange && onPersonaChange('')}
                    activeFilterCount={selectedPersonaId ? 1 : 0}
                >
                    <DashboardPersonaFilter
                        personas={personas}
                        selectedPersonaId={selectedPersonaId || ''}
                        onChange={onPersonaChange}
                        responseCounts={personaResponseCounts}
                    />
                </DashboardFilterBar>
            )}
            {!hideControls && displayMode === 'navigator' && fields.length > 1 && (
                <div className="analysis-card-navigator-row" data-copy-hide>
                    <AnalysisItemNavigator
                        items={fields}
                        visible={visible}
                        setVisible={setVisible}
                    />
                    {renderItemAction && currentField && (
                        <div className="analysis-card-item-pin">
                            {renderItemAction(currentField, research)}
                        </div>
                    )}
                </div>
            )}
            <div className="analysis-card-visualisation">
                {displayMode === 'stacked' ? (
                    fields.length === 0
                        ? <p className="analysis-card-empty">{emptyText}</p>
                        : fields.map(field => (
                            <div key={field.ID} className="analysis-card-visualisation-stacked-item">
                                {renderOneField(field)}
                            </div>
                        ))
                ) : (
                    currentField
                        ? renderOneField(currentField)
                        : <p className="analysis-card-empty">{emptyText}</p>
                )}
            </div>
            {!hideControls && dashboardItem && currentField && (
                <DashboardCardOptions
                    item={dashboardItem}
                    fieldType={resolvedFieldType}
                    currentGraphType={resolveGraphType(currentField)}
                    subItems={subItems}
                    onSave={saveIndicatorOptions}
                    leadingBadge={fields.length > 1 ? `${visible + 1} / ${fields.length}` : null}
                />
            )}
        </EmptyStateProvider>
    )
}

export default ResearchVisualization
